import { useQuery, UseQueryResult, useQueryClient } from '@tanstack/react-query';
import {
  getCompletenessReport,
  getReadinessReport,
  getLocalizationQualityReport,
  getEnrichmentVelocityReport,
  getChangeHistoryReport,
  getAnalyticsRefreshStatus,
  CompletenessReport,
  ReadinessReport,
  LocalizationQualityReport,
  EnrichmentVelocityReport,
  ChangeHistoryReport,
  getAnalyticsErrorMessage,
  getCompletenessReportWithFilters,
  getLocaleAttributes,
} from '@/services/analyticsService';

// Type for axios errors
interface AxiosError extends Error {
  response?: {
    status: number;
    data: any;
  };
}

// Type guard to check if error is an AxiosError
function isAxiosError(error: any): error is AxiosError {
  return error && typeof error === 'object' && 'response' in error;
}

// Helper function to check if error is a 4xx client error
function isClientError(error: any): boolean {
  return (
    isAxiosError(error) &&
    error.response?.status !== undefined &&
    error.response.status >= 400 &&
    error.response.status < 500
  );
}

// ============================================================================
// ANALYTICS REPORTS HOOKS
// ============================================================================

/**
 * Hook for fetching completeness report data
 *
 * Returns comprehensive completeness metrics including:
 * - Overall completeness percentage
 * - Attribute-level breakdown with completion stats
 * - Category-level breakdown with completion percentages
 */
export function useCompletenessReport(): UseQueryResult<CompletenessReport, AxiosError> {
  return useQuery({
    queryKey: ['analytics', 'completeness-report', 'v2'],
    queryFn: getCompletenessReport,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (isClientError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    meta: {
      errorMessage: 'Failed to load completeness report',
    },
  });
}

/**
 * Hook for fetching readiness report data
 *
 * Returns channel readiness metrics including:
 * - Channel-specific readiness percentage
 * - Total and completed attributes count
 * - Channel breakdown and field-level details
 */
export function useReadinessReport(
  channel: string = 'default'
): UseQueryResult<ReadinessReport, AxiosError> {
  return useQuery({
    queryKey: ['analytics', 'readiness-report', channel],
    queryFn: () => getReadinessReport(channel),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: (failureCount, error) => {
      if (isClientError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    meta: {
      errorMessage: 'Failed to load readiness report',
    },
  });
}

/**
 * Hook for fetching localization quality report data
 *
 * Returns localization coverage statistics for different locales:
 * - Translation coverage percentage per locale
 * - Total and translated attributes count
 * - Quality scores and pending translations
 */
export function useLocalizationQualityReport(): UseQueryResult<
  LocalizationQualityReport[],
  AxiosError
> {
  return useQuery({
    queryKey: ['analytics', 'localization-quality-report'],
    queryFn: getLocalizationQualityReport,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: (failureCount, error) => {
      if (isClientError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    meta: {
      errorMessage: 'Failed to load localization quality report',
    },
  });
}

/**
 * Hook for fetching enrichment velocity report data
 *
 * Returns enrichment velocity metrics:
 * - Edit count and average time to publish
 * - Products enriched count
 * - Velocity trends and top editors
 */
export function useEnrichmentVelocityReport(): UseQueryResult<
  EnrichmentVelocityReport,
  AxiosError
> {
  return useQuery({
    queryKey: ['analytics', 'enrichment-velocity-report'],
    queryFn: getEnrichmentVelocityReport,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: (failureCount, error) => {
      if (isClientError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    meta: {
      errorMessage: 'Failed to load enrichment velocity report',
    },
  });
}

/**
 * Hook for fetching change history report data
 *
 * Returns historical change tracking data with filtering support:
 * - Date range filtering
 * - User filtering
 * - Entity type filtering
 * - Action filtering
 */
export function useChangeHistoryReport(params?: {
  from?: string;
  to?: string;
  username?: string[];
  entity?: string[];
  action?: string[];
  limit?: number;
}): UseQueryResult<ChangeHistoryReport[], AxiosError> {
  return useQuery({
    queryKey: ['analytics', 'change-history-report', params],
    queryFn: () => getChangeHistoryReport(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: (failureCount, error) => {
      if (isClientError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    meta: {
      errorMessage: 'Failed to load change history report',
    },
  });
}

/**
 * Hook for fetching analytics refresh status
 *
 * Returns information about the last analytics refresh:
 * - Refresh status and timing
 * - Data freshness indicators
 * - Health status
 */
export function useAnalyticsRefreshStatus() {
  return useQuery({
    queryKey: ['analytics', 'refresh-status'],
    queryFn: getAnalyticsRefreshStatus,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 60 * 1000, // Refetch every minute
    retry: (failureCount, error) => {
      if (isClientError(error)) {
        return false;
      }
      return failureCount < 2;
    },
    meta: {
      errorMessage: 'Failed to load analytics refresh status',
    },
  });
}

/**
 * Hook for fetching completeness report data with filters
 *
 * Returns comprehensive completeness metrics with filtering support including:
 * - Overall completeness percentage
 * - Attribute-level breakdown with completion stats
 * - Category-level breakdown with completion percentages
 */
export function useCompletenessReportWithFilters(filters?: {
  locale?: string;
  category?: string;
  channel?: string;
  family?: string;
  from?: string;
  to?: string;
}): UseQueryResult<CompletenessReport, AxiosError> {
  return useQuery({
    queryKey: ['analytics', 'completeness-report-filtered', filters],
    queryFn: () => getCompletenessReportWithFilters(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (isClientError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    meta: {
      errorMessage: 'Failed to load filtered completeness report',
    },
  });
}

/**
 * Hook for fetching locale attributes data
 *
 * Returns the top 5 least completed attributes for each locale or a specific locale:
 * - Attribute name and completion percentage
 * - Missing count and total count
 */
export function useLocaleAttributes(filters?: { locale?: string; family?: string }): UseQueryResult<
  Record<
    string,
    Array<{
      name: string;
      completion: number;
      missing: number;
      total: number;
    }>
  >,
  AxiosError
> {
  return useQuery({
    queryKey: ['analytics', 'locale-attributes', filters],
    queryFn: () => getLocaleAttributes(filters),
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: (failureCount, error) => {
      if (isClientError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    meta: {
      errorMessage: 'Failed to load locale attributes data',
    },
  });
}

// ============================================================================
// UTILITY HOOKS
// ============================================================================

/**
 * Hook that provides error handling utilities for analytics queries
 */
export function useAnalyticsErrorHandler() {
  const getErrorMessage = (error: any): string => {
    return getAnalyticsErrorMessage(error);
  };

  const isRetryableError = (error: any): boolean => {
    // Don't retry on 4xx errors (client errors)
    if (error?.response?.status >= 400 && error?.response?.status < 500) {
      return false;
    }

    // Retry on 5xx errors (server errors) and network errors
    return true;
  };

  return {
    getErrorMessage,
    isRetryableError,
  };
}

/**
 * Hook that provides a unified loading state for all analytics reports
 */
export function useAnalyticsLoadingState() {
  const completenessQuery = useCompletenessReport();
  const readinessQuery = useReadinessReport();
  const localizationQuery = useLocalizationQualityReport();
  const velocityQuery = useEnrichmentVelocityReport();
  const refreshStatusQuery = useAnalyticsRefreshStatus();

  const isLoading =
    completenessQuery.isLoading ||
    readinessQuery.isLoading ||
    localizationQuery.isLoading ||
    velocityQuery.isLoading ||
    refreshStatusQuery.isLoading;

  const hasError =
    completenessQuery.isError ||
    readinessQuery.isError ||
    localizationQuery.isError ||
    velocityQuery.isError ||
    refreshStatusQuery.isError;

  const errors = [
    completenessQuery.error,
    readinessQuery.error,
    localizationQuery.error,
    velocityQuery.error,
    refreshStatusQuery.error,
  ].filter(Boolean);

  return {
    isLoading,
    hasError,
    errors,
    queries: {
      completeness: completenessQuery,
      readiness: readinessQuery,
      localization: localizationQuery,
      velocity: velocityQuery,
      refreshStatus: refreshStatusQuery,
    },
  };
}

/**
 * Hook that provides cache invalidation for analytics reports
 * Useful for refreshing data after analytics refresh operations
 */
export function useInvalidateAnalyticsCache() {
  const queryClient = useQueryClient();

  return () => {
    // Invalidate all analytics-related queries
    queryClient.invalidateQueries({ queryKey: ['analytics'] });

    // Specifically invalidate localization quality data (critical after fix)
    queryClient.invalidateQueries({
      queryKey: ['analytics', 'localization-quality-report'],
    });
    queryClient.invalidateQueries({
      queryKey: ['analytics', 'locale-attributes'],
    });

    // Invalidate other analytics reports
    queryClient.invalidateQueries({
      queryKey: ['analytics', 'completeness-report'],
    });
    queryClient.invalidateQueries({
      queryKey: ['analytics', 'readiness-report'],
    });
    queryClient.invalidateQueries({
      queryKey: ['analytics', 'enrichment-velocity-report'],
    });
    queryClient.invalidateQueries({
      queryKey: ['analytics', 'change-history-report'],
    });
    queryClient.invalidateQueries({ queryKey: ['analytics', 'refresh-status'] });

    // Invalidate filtered reports
    queryClient.invalidateQueries({
      queryKey: ['analytics', 'completeness-report-filtered'],
    });

    // Invalidate dashboard analytics
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };
}
